<script lang="ts">
  import {onMount, onDestroy} from 'svelte';
  import {fly} from 'svelte/transition';
  import {router} from 'yrv';
  import InlineSVG from 'svelte-inline-svg';

  import {projects} from '../../stores/projects';
  import {auth, user} from '../../stores/user';
  import {selectedProjectIds} from '../../stores/selectedProjects';
  import {
    searchText,
    typeFilter,
    confidentialityFilter,
    visibilityFilter,
    diamondFilter,
    customersFilter,
    teamFilter,
    fieldsFilter,
    topicsFilter,
    servicesFilter,
    showDrafts,
    tasksFilter,
    technologiesFilter,
    methodsFilter,
    formatsFilter,
    minValueInTimeFilter,
    maxValueInTimeFilter,
    minValueInPriceFilter,
    maxValueInPriceFilter,
    startDateFilter,
    endDateFilter,
    dateRangeFilter,
    sortDirection
  } from '../../stores/filter-and-sort';

  import filterAndSortProjects from '../../utils/filter-and-sort-projects';
  import slides from '../../utils/slides';

  import slidesIcon from '../../assets/icons/images.svg';

  import Button from '../button/Button.svelte';
  import ListHeader from '../list-header/ListHeader.svelte';
  import LazyProjectsList from '../lazy-projects-list/LazyProjectsList.svelte';

  let slideUrl = '';
  let slideOpened = false;
  let slideError = false;
  let isGeneratingSlides = false;

  function resetAllFilters() {
    // General filters
    searchText.reset();
    typeFilter.reset();
    confidentialityFilter.reset();
    visibilityFilter.reset();
    diamondFilter.reset();
    customersFilter.reset();
    teamFilter.reset();
    fieldsFilter.reset();
    topicsFilter.reset();
    servicesFilter.reset();
    startDateFilter.reset();
    endDateFilter.reset();
    showDrafts.reset();
    // Development filters
    tasksFilter.reset();
    technologiesFilter.reset();
    // Consulting filters
    formatsFilter.reset();
    methodsFilter.reset();
    minValueInTimeFilter.reset();
    maxValueInTimeFilter.reset();
    minValueInPriceFilter.reset();
    maxValueInPriceFilter.reset();
  }

  // Set initial filters from query parameters
  onMount(() => {
    const queryParameterEntries = Object.entries($router.query);

    if (!queryParameterEntries.length) {
      return;
    }

    resetAllFilters();

    // Set filters to query parameters
    Object.entries($router.query).forEach(([type, value]) => {
      if (!value) {
        return;
      }

      switch (type) {
        // General filters
        case 'search':
          $searchText = value;
          break;
        case 'type':
          $typeFilter = value;
          break;
        case 'confidentiality':
          $confidentialityFilter = value;
          break;
        case 'visibility':
          $visibilityFilter = value;
          break;
        case 'diamond':
          $diamondFilter = Array.isArray(value) ? value : [value];
          break;
        case 'customers':
          $customersFilter = Array.isArray(value) ? value : [value];
          break;
        case 'team':
          $teamFilter = Array.isArray(value) ? value : [value];
          break;
        case 'fields':
          $fieldsFilter = Array.isArray(value) ? value : [value];
          break;
        case 'topics':
          $topicsFilter = Array.isArray(value) ? value : [value];
          break;
        case 'services':
          $servicesFilter = Array.isArray(value) ? value : [value];
          break;
        case 'startDate':
          $startDateFilter = new Date(value);
          break;
        case 'endDate':
          $endDateFilter = new Date(value);
          break;
        // Development filters
        case 'tasks':
          $tasksFilter = Array.isArray(value) ? value : [value];
          break;
        case 'technologies':
          $technologiesFilter = Array.isArray(value) ? value : [value];
          break;
        // Consulting filters
        case 'formats':
          $formatsFilter = Array.isArray(value) ? value : [value];
          break;
        case 'methods':
          $methodsFilter = Array.isArray(value) ? value : [value];
          break;
        case 'minValueInTime':
          $minValueInTimeFilter = value;
          break;
        case 'maxValueInTime':
          $maxValueInTimeFilter = value;
          break;
        case 'minValueInPrice':
          $minValueInPriceFilter = value;
          break;
        case 'maxValueInPrice':
          $maxValueInPriceFilter = value;
          break;
        default:
          break;
      }
    });
  });

  // Reset project selection if user logged out
  const unsubscribeUserStore = user.subscribe(async loggedInUser => {
    if (!loggedInUser) {
      selectedProjectIds.set([]);
    }
  });

  onDestroy(unsubscribeUserStore);

  $: filterValues = {
    searchText: $searchText,
    typeFilter: $typeFilter,
    confidentialityFilter: $confidentialityFilter,
    visibilityFilter: $visibilityFilter,
    diamondFilter: $diamondFilter,
    customersFilter: $customersFilter,
    teamFilter: $teamFilter,
    fieldsFilter: $fieldsFilter,
    topicsFilter: $topicsFilter,
    servicesFilter: $servicesFilter,
    dateRangeFilter: $dateRangeFilter,
    showDrafts: $showDrafts,
    tasksFilter: $tasksFilter,
    technologiesFilter: $technologiesFilter,
    formatsFilter: $formatsFilter,
    methodsFilter: $methodsFilter,
    valueInTimeFilter: {min: $minValueInTimeFilter, max: $maxValueInTimeFilter},
    valueInPriceFilter: {
      min: $minValueInPriceFilter,
      max: $maxValueInPriceFilter
    }
  };

  // Filter and sort projects
  $: filteredAndSortedProjects = filterAndSortProjects(
    [...$projects.entries()].map(([id, project]) => ({...project, id})),
    filterValues,
    $sortDirection,
    $selectedProjectIds
  );

  // Selects / deselects the project with the given id
  function toggleProjectSelection({detail: projectId}: CustomEvent<string>) {
    slideUrl = '';
    selectedProjectIds.update(ids =>
      ids.includes(projectId)
        ? ids.filter(id => id !== projectId)
        : [...ids, projectId]
    );
  }

  const onSlideExportCompleted = () => {
    isGeneratingSlides = false;

    if (slideUrl && !slideOpened) {
      window.open(slideUrl, '_blank');
    }
  };

  const onSlideExportGotUrl = (url: string) => {
    slideUrl = url;
  };

  const onSlideExportError = () => {
    isGeneratingSlides = false;
    slideError = true;
  };

  const exportToGoogleSlides = async (isRetry?: boolean) => {
    if ($selectedProjectIds.length && (!isGeneratingSlides || isRetry)) {
      isGeneratingSlides = true;
      slideUrl = '';
      slideOpened = false;
      slideError = false;

      const selectedProjects = [...$projects.entries()]
        .map(([id, project]) => ({...project, id}))
        .filter(({id}) => $selectedProjectIds.includes(id));

      // Request access token if not available, before talking to the Google API
      if (!gapi.client.getToken()) {
        try {
          await auth.requestAccessToken();
        } catch (error) {
          console.error(error);
          onSlideExportError();
          return;
        }
      }

      slides.createPresentation(
        selectedProjects,
        onSlideExportCompleted,
        onSlideExportGotUrl,
        (error: any) => {
          // Revoke API token and retry slides export once if failed with status 401 Unauthorized
          if (!isRetry) {
            const errorStatus = typeof error === 'object' && error.status;

            if (errorStatus === 401) {
              gapi.client.setToken(null);
              exportToGoogleSlides(true);
              return;
            }
          }

          onSlideExportError();
        }
      );
    }
  };

  $: isInSelectionMode = Boolean($selectedProjectIds.length);
</script>

<style lang="postcss">
  footer {
    position: fixed;
    left: 0;
    bottom: 0;
    right: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.5rem;
    border-top: 1px solid var(--ubilabs-gray-light);
    background-color: var(--ubilabs-white);
    box-shadow: var(--ubilabs-box-shadow);
  }

  @media (min-width: 640px) {
    footer {
      padding: 0.5rem 1rem;
    }
  }

  @media (min-width: 768px) {
    footer {
      padding: 1rem 1.5rem;
    }
  }

  footer > div {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  @media (max-width: 639px) {
    .selectionWrapper {
      width: 100%;
    }

    .selectionWrapper > :global(button) {
      justify-content: center;
      flex-grow: 1;
    }
  }

  .projectSelection {
    flex-grow: 1;
    text-align: center;
  }

  @media (min-width: 640px) {
    .projectSelection {
      margin: 0 0.5rem;
    }
  }

  .projectSelection > span {
    display: none;
  }

  @media (min-width: 768px) {
    .projectSelection > span {
      display: inline;
    }
  }

  .exportContainer {
    flex-direction: column-reverse;
    justify-content: end;
    gap: 0.25rem;
    width: 100%;
    margin-top: 0.5rem;
  }

  @media (min-width: 640px) {
    .exportContainer {
      flex-direction: row;
      width: auto;
      margin-top: 0;
    }
  }

  .errorMessage {
    display: block;
    margin-top: 0.5rem;
    color: var(--ubilabs-error-color);
  }

  @media (min-width: 640px) {
    .errorMessage {
      margin-top: 0;
    }
  }
</style>

<ListHeader
  numberOfProjectsTotal={$projects.size}
  numberOfProjectsFiltered={filteredAndSortedProjects.length}
  on:resetFilters={resetAllFilters}
/>

<LazyProjectsList
  projects={filteredAndSortedProjects}
  selectedProjectIds={$selectedProjectIds}
  on:toggle={toggleProjectSelection}
/>

{#if isInSelectionMode}
  <footer transition:fly={{y: 80, duration: 300}}>
    <div class="selectionWrapper">
      <Button
        variant="secondary"
        on:click={event => {
          event.stopPropagation();
          selectedProjectIds.set(
            filteredAndSortedProjects.map(project => project.id)
          );
        }}
      >
        Select all
      </Button>

      <Button variant="secondary" on:click={() => selectedProjectIds.set([])}>
        Clear selection
      </Button>
    </div>

    <span class="projectSelection">
      {$selectedProjectIds.length}
      <span>{$selectedProjectIds.length > 1 ? 'projects' : 'project'} </span>
      selected
    </span>

    <div class="exportContainer">
      {#if slideError}
        <small class="errorMessage">Export failed. Please try again.</small>
      {/if}

      {#if slideUrl && !slideError}
        <a
          href={slideUrl}
          target="_blank"
          on:click={() => (slideOpened = true)}
        >
          Open Slides
        </a>
      {/if}

      <Button
        isLoading={isGeneratingSlides}
        on:click={() => exportToGoogleSlides()}
        aria-labelledby="slides-export-label"
      >
        <span slot="icon"><InlineSVG src={slidesIcon} /></span>
        <span id="slides-export-label">Export to Google Slides</span>
      </Button>
    </div>
  </footer>
{/if}
